import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useQuery, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  TextField,
  Button,
  InputAdornment,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../../layouts/loading';
import { getEscalationAnalytics } from '../../../services/escalationService';
import { isSuperAdmin, isAdmin, isManager, isHR } from '../../../utils/share';

const queryClient = new QueryClient();

const EscalationAnalyticsContent = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state?.login?.user || null);
  const companyId = currentUser?.companyId;
  const roleId = currentUser?.userRoleId;
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
  });
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  // Ref to store the debounce timeout
  const searchTimeoutRef = useRef(null);
  // Ref to store the search input element
  const searchInputRef = useRef(null);
  // Track if search input should maintain focus
  const shouldMaintainFocusRef = useRef(false);

  // Check if user has access to analytics (Super Admin, Admin, Manager, HR only)
  const hasAccess = isSuperAdmin() || isAdmin() || isManager() || isHR();

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Escalations',
      path: '/escalations',
    },
    {
      label: 'Analytics',
      path: '',
    },
  ];

  // Effect to debounce search term
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setAppliedSearchTerm(searchTerm);
      setPagination((prev) => ({ page: 0, rowsPerPage: prev.rowsPerPage })); // Reset to first page on search
    }, 500); // 500ms debounce delay

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const {
    data: analyticsResponse,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      'escalationAnalytics',
      companyId,
      appliedStartDate,
      appliedEndDate,
      appliedSearchTerm,
      pagination.page,
      pagination.rowsPerPage,
    ],
    queryFn: async () => {
      try {
        const response = await getEscalationAnalytics(
          roleId === 7 ? companyId : null,
          appliedStartDate ? dayjs(appliedStartDate).format('YYYY-MM-DD') : null,
          appliedEndDate ? dayjs(appliedEndDate).format('YYYY-MM-DD') : null,
          appliedSearchTerm || null,
          {
            page: pagination.page + 1, // Backend uses 1-based page
            pageSize: pagination.rowsPerPage,
          }
        );
        return response?.data?.data || { rows: [], count: 0 };
      } catch (err) {
        console.error('Error fetching escalation analytics:', err);
        throw err;
      }
    },
    enabled: hasAccess, // Only fetch if user has access
    staleTime: 30000, // Cache for 30 seconds
    keepPreviousData: true, // Keep previous data while fetching to prevent re-renders
  });

  // Effect to maintain focus during query - use useLayoutEffect for synchronous execution
  // Must be after useQuery to access isFetching
  useLayoutEffect(() => {
    if (shouldMaintainFocusRef.current && searchInputRef.current) {
      // Check if input is not currently focused
      const inputElement = searchInputRef.current;
      const isInputFocused = document.activeElement === inputElement || inputElement.contains(document.activeElement);

      if (!isInputFocused) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          if (inputElement && shouldMaintainFocusRef.current) {
            inputElement.focus();
          }
        });
      }
    }
  }, [isFetching, appliedSearchTerm, analyticsResponse]);

  const analytics = analyticsResponse?.rows || [];
  const totalCount = analyticsResponse?.count || 0;

  // Show access denied if user doesn't have permission (after hooks)
  if (!hasAccess) {
    return (
      <>
        <BreadCrumb breadcrumbs={breadcrumbs} />
        <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error" align="center">
                Access Denied
              </Typography>
              <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                You do not have permission to view escalation analytics. Only Super Admin, Admin, Manager, and HR can
                access this page.
              </Typography>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:arrow-back-fill" />}
                  onClick={() => navigate('/escalations')}
                >
                  Back to Escalations
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </>
    );
  }

  const handleApplyFilters = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchTerm('');
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setAppliedSearchTerm('');
    setPagination({ page: 0, rowsPerPage: pagination.rowsPerPage });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy hh:mm a');
    } catch (error) {
      return dateString;
    }
  };

  // Calculate totals from current page data
  const totalProjects = totalCount; // Use total count from backend
  const totalEscalations = analytics.reduce((sum, item) => sum + item.totalEscalations, 0);
  const totalCritical = analytics.reduce((sum, item) => sum + item.critical, 0);
  const totalHigh = analytics.reduce((sum, item) => sum + item.high, 0);

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPagination({ page: 0, rowsPerPage: newRowsPerPage });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    // Debouncing is handled by useEffect
    // Maintain focus while typing
    shouldMaintainFocusRef.current = true;
  };

  const handleSearchFocus = () => {
    shouldMaintainFocusRef.current = true;
  };

  const handleSearchBlur = (event) => {
    // Only clear the flag if user explicitly clicked outside
    // Don't clear if it's just a temporary blur during re-render
    setTimeout(() => {
      const activeElement = document.activeElement;
      const inputElement = searchInputRef.current;

      // If focus moved to something else (not the input), clear the flag
      if (activeElement !== inputElement && !inputElement?.contains(activeElement)) {
        shouldMaintainFocusRef.current = false;
      }
    }, 150);
  };

  if (isLoading) {
    return <Loading open={isLoading} />;
  }

  if (error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'Error loading escalation analytics';

    return (
      <>
        <BreadCrumb breadcrumbs={breadcrumbs} />
        <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error" align="center" gutterBottom>
                {errorMessage}
              </Typography>
              {error?.response?.data?.status && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                  Status: {error.response.data.status}
                </Typography>
              )}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:refresh-fill" />}
                  onClick={() => window.location.reload()}
                  sx={{ mr: 2 }}
                >
                  Retry
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:arrow-back-fill" />}
                  onClick={() => navigate('/escalations')}
                >
                  Back to Escalations
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Escalation Analytics</title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Box sx={{ maxWidth: 1400, margin: 'auto', padding: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Typography variant="h4">Escalation Analytics - Report</Typography>
        </Stack>

        {/* Search and Date Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  inputRef={searchInputRef}
                  fullWidth
                  size="small"
                  label="Search by Project Name"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  placeholder="Search projects..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    inputFormat="DD-MMM-YYYY"
                    maxDate={endDate || dayjs()}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    inputFormat="DD-MMM-YYYY"
                    minDate={startDate || undefined}
                    maxDate={dayjs()}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    startIcon={<Iconify icon="eva:search-fill" />}
                    disabled={!startDate || !endDate}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                    disabled={!startDate && !endDate && !searchTerm}
                  >
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Projects with Escalations
                </Typography>
                <Typography variant="h4">{totalProjects}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Escalations
                </Typography>
                <Typography variant="h4">{totalEscalations}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'error.lighter' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Critical Escalations
                </Typography>
                <Typography variant="h4" color="error.main">
                  {totalCritical}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.lighter' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  High Severity Escalations
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {totalHigh}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Projects Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Projects Requiring Focus (Sorted by Severity)
            </Typography>
            {analytics.length > 0 ? (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Project Name</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Critical</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>High</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Medium</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Low</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Latest Escalation</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Project Status</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.map((project) => (
                        <TableRow key={project.projectId} hover>
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {project.projectName}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={project.totalEscalations} color="primary" variant="outlined" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            {project.critical > 0 ? (
                              <Chip label={project.critical} color="error" size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {project.high > 0 ? (
                              <Chip label={project.high} color="warning" size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {project.medium > 0 ? (
                              <Chip label={project.medium} color="info" size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {project.low > 0 ? (
                              <Chip label={project.low} color="default" size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(project.latestEscalationDate)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={project.projectStatus || 'unknown'}
                              color={
                                project.projectStatus === 'active'
                                  ? 'success'
                                  : project.projectStatus === 'on-hold'
                                  ? 'warning'
                                  : 'default'
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalCount > 0 && (
                  <TablePaginationCustom
                    count={totalCount}
                    page={pagination.page}
                    rowsPerPage={pagination.rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No escalations found. All projects are running smoothly!
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Insights Section */}
        {analytics.length > 0 && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Insights
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                {totalCritical > 0 && (
                  <Typography variant="body2" color="error.main">
                    ⚠️ <strong>{totalCritical} critical escalation(s)</strong> require immediate attention across{' '}
                    {analytics.filter((p) => p.critical > 0).length} project(s).
                  </Typography>
                )}
                {totalHigh > 0 && (
                  <Typography variant="body2" color="warning.main">
                    ⚡ <strong>{totalHigh} high severity escalation(s)</strong> need to be addressed soon across{' '}
                    {analytics.filter((p) => p.high > 0).length} project(s).
                  </Typography>
                )}
                {analytics.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    📊 <strong>{analytics.length} project(s)</strong> have escalations that need focus and review.
                  </Typography>
                )}
                {analytics.filter((p) => p.critical > 0 || p.high > 0).length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    🎯 Top severity projects:{' '}
                    {analytics
                      .filter((p) => p.critical > 0 || p.high > 0)
                      .slice(0, 3)
                      .map((p) => p.projectName)
                      .join(', ')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>
    </>
  );
};

const EscalationAnalytics = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <EscalationAnalyticsContent />
    </QueryClientProvider>
  );
};

export default EscalationAnalytics;
